import { useCallback, useEffect, useRef, useState } from 'react';

export const getScrollableTabState = ({
  scrollLeft = 0,
  scrollWidth = 0,
  clientWidth = 0,
} = {}) => ({
  canScrollLeft: scrollLeft > 0,
  // Keep the existing one-pixel tolerance so fractional layout widths do not
  // show a right-scroll affordance when the tab strip is effectively at the end.
  canScrollRight: scrollLeft + clientWidth < scrollWidth - 1,
});

export const useScrollableTabs = ({ scrollAmount = 150, refreshKey = null } = {}) => {
  const tabsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkTabsScroll = useCallback(() => {
    if (!tabsRef.current) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const nextState = getScrollableTabState(tabsRef.current);
    setCanScrollLeft(nextState.canScrollLeft);
    setCanScrollRight(nextState.canScrollRight);
  }, []);

  useEffect(() => {
    const scheduleMeasure = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    const cancelMeasure = window.cancelAnimationFrame || window.clearTimeout;
    const measureId = scheduleMeasure(checkTabsScroll);

    window.addEventListener('resize', checkTabsScroll);
    return () => {
      cancelMeasure(measureId);
      window.removeEventListener('resize', checkTabsScroll);
    };
  }, [checkTabsScroll, refreshKey]);

  const scrollTabs = useCallback((direction) => {
    if (!tabsRef.current) {
      return;
    }

    tabsRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, [scrollAmount]);

  return {
    tabsRef,
    canScrollLeft,
    canScrollRight,
    checkTabsScroll,
    scrollTabs,
  };
};
